
import React from 'react';
import { Loader2 } from 'lucide-react';
import { ContactFormData, ContactFormErrors } from './contactFormTypes';

interface ContactFormFieldsProps {
  formData: ContactFormData;
  errors: ContactFormErrors;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  isSubmitting: boolean;
}

const ContactFormFields = ({ formData, errors, handleChange, isSubmitting }: ContactFormFieldsProps) => {
  // Helper functions for validation
  const getInputClassName = (fieldName: keyof ContactFormErrors) => {
    return `w-full border rounded-md p-3 transition-all duration-200 focus:ring-2 focus:ring-primary/30 focus:outline-none bg-white ${
      errors[fieldName] ? 'border-accent' : 'border-[#D3C4B0]'
    }`;
  };
  
  return (
    <>
      <div>
        <label htmlFor="name" className="block mb-2 font-medium text-base font-montserrat text-[#4A3F3B]">
          Name <span className="text-accent">*</span>
        </label>
        <input 
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={getInputClassName('name')}
          disabled={isSubmitting}
          placeholder="Your name"
          aria-invalid={errors.name ? "true" : "false"}
          aria-describedby={errors.name ? "name-error" : undefined}
          maxLength={100}
          required
          autoComplete="name"
          // Real-time validation
          onBlur={(e) => {
            if (e.target.value && e.target.value.trim().length < 2) {
              const evt = {
                target: {
                  name: 'name',
                  value: e.target.value
                }
              } as React.ChangeEvent<HTMLInputElement>;
              handleChange(evt);
            }
          }}
        />
        {errors.name && (
          <span id="name-error" className="text-sm text-accent mt-1 block" role="alert">
            {errors.name}
          </span>
        )}
      </div>
      
      <div>
        <label htmlFor="email" className="block mb-2 font-medium text-base font-montserrat text-[#4A3F3B]">
          Email address <span className="text-accent">*</span>
        </label>
        <input 
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={getInputClassName('email')}
          disabled={isSubmitting}
          placeholder="your.email@example.com"
          aria-invalid={errors.email ? "true" : "false"}
          aria-describedby={errors.email ? "email-error" : undefined}
          maxLength={254}
          required
          autoComplete="email"
          pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$"
        />
        {errors.email && (
          <span id="email-error" className="text-sm text-accent mt-1 block" role="alert">
            {errors.email}
          </span>
        )}
      </div>
      
      <div>
        <label htmlFor="phone" className="block mb-2 font-medium text-base font-montserrat text-[#4A3F3B]">
          Phone <span className="text-text/60 text-sm font-normal">(optional)</span>
        </label>
        <input 
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone || ''}
          onChange={handleChange}
          className={getInputClassName('phone')}
          disabled={isSubmitting}
          placeholder="(123) 456-7890"
          aria-invalid={errors.phone ? "true" : "false"}
          aria-describedby={errors.phone ? "phone-error" : undefined}
          autoComplete="tel"
          // HTML5 pattern for US phone numbers (optional)
          pattern="(\+?1[ -]?)?(\([0-9]{3}\)|[0-9]{3})[ -]?[0-9]{3}[ -]?[0-9]{4}"
        />
        {errors.phone && (
          <span id="phone-error" className="text-sm text-accent mt-1 block" role="alert">
            {errors.phone}
          </span>
        )}
      </div>
      
      <div>
        <label htmlFor="message" className="block mb-2 font-medium text-base font-montserrat text-[#4A3F3B]">
          Message <span className="text-text/60 text-sm font-normal">(optional)</span>
        </label>
        <textarea 
          id="message"
          name="message"
          value={formData.message || ''}
          onChange={handleChange}
          rows={5}
          className={getInputClassName('message')}
          disabled={isSubmitting}
          placeholder="How can we help you?"
          aria-invalid={errors.message ? "true" : "false"}
          aria-describedby={errors.message ? "message-error" : undefined}
          maxLength={1000}
        />
        {errors.message && (
          <span id="message-error" className="text-sm text-accent mt-1 block" role="alert">
            {errors.message}
          </span>
        )}
        <div className="text-right text-xs text-gray-500 mt-1">
          {formData.message ? formData.message.length : 0}/1000
        </div>
      </div>
      
      <div className="flex items-start gap-3">
        <div className="flex items-center h-5 mt-1">
          <input
            id="consent"
            name="consent"
            type="checkbox"
            checked={formData.consent}
            onChange={handleChange}
            className="w-5 h-5 border border-[#D3C4B0] rounded text-accent focus:ring-accent transition-all duration-200"
            disabled={isSubmitting}
            aria-invalid={errors.consent ? "true" : "false"}
            aria-describedby={errors.consent ? "consent-error" : undefined}
            required
          />
        </div>
        <label 
          htmlFor="consent" 
          className={`text-base font-montserrat ${errors.consent ? 'text-accent' : 'text-text/80'}`}
        >
          I agree to receive updates from Cats About Town. <span className="text-accent">*</span>
        </label>
      </div>
      {errors.consent && (
        <span id="consent-error" className="text-sm text-accent block" role="alert">
          {errors.consent}
        </span>
      )}
      
      <button 
        type="submit" 
        className="btn-accent w-full md:w-auto flex items-center justify-center py-3 px-8 rounded-md text-base font-montserrat font-semibold shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 hover:text-white"
        disabled={isSubmitting}
        aria-busy={isSubmitting}
        data-testid="submit-contact-form"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" aria-hidden="true" />
            Sending...
          </>
        ) : (
          'Send Message'
        )}
      </button>
    </>
  );
};

export default ContactFormFields;
